// SEO Utilities for Utah Realty Photos
// Use these functions to set page metadata dynamically

/**
 * Update page title and meta description
 * Call this in useEffect when component mounts
 */
export const updatePageMeta = ({ title, description, keywords, canonical }) => {
  // Update title
  if (title) {
    document.title = title;
    
    // Update Open Graph title
    updateMetaTag('property', 'og:title', title);
    updateMetaTag('name', 'twitter:title', title);
  }
  
  // Update description
  if (description) {
    updateMetaTag('name', 'description', description);
    updateMetaTag('property', 'og:description', description);
    updateMetaTag('name', 'twitter:description', description);
  }
  
  // Update keywords
  if (keywords) {
    updateMetaTag('name', 'keywords', keywords);
  }
  
  // Update canonical URL
  if (canonical) {
    updateCanonicalLink(canonical);
  }
  
  // Open Graph URL = the canonical (2026-10-17). It used to be
  // window.location.href, so a shared link with ?utm=… or #… became the URL
  // the share attached to, and it disagreed with the server-rendered HTML.
  const pageUrl = canonical || `${window.location.origin}${window.location.pathname}`;
  updateMetaTag('property', 'og:url', pageUrl);
  updateMetaTag('name', 'twitter:url', pageUrl);
};

/**
 * Helper function to update or create meta tags
 */
const updateMetaTag = (attribute, attributeValue, content) => {
  let element = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
  
  if (element) {
    element.setAttribute('content', content);
  } else {
    element = document.createElement('meta');
    element.setAttribute(attribute, attributeValue);
    element.setAttribute('content', content);
    document.head.appendChild(element);
  }
};

/**
 * Update canonical link
 */
const updateCanonicalLink = (url) => {
  let link = document.querySelector('link[rel="canonical"]');
  
  if (link) {
    link.setAttribute('href', url);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    document.head.appendChild(link);
  }
};

/**
 * Add structured data (JSON-LD)
 */
export const addStructuredData = (data) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  script.id = 'structured-data';
  
  // Remove existing structured data
  const existing = document.getElementById('structured-data');
  if (existing) {
    existing.remove();
  }
  
  document.head.appendChild(script);
};

/**
 * Add or replace a JSON-LD block by id (2026-10-16). `null` removes it.
 */
export const setJsonLd = (id, data) => {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * public/index.html carries the HOMEPAGE's BreadcrumbList and FAQPage on every
 * route. A page with its own (a blog post) removes those shell copies so a
 * crawler sees one of each. Pack 2's server renderer emits per-route blocks.
 */
export const removeShellJsonLd = (types) => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('script[type="application/ld+json"]:not([id])').forEach((el) => {
    try {
      const t = JSON.parse(el.textContent || '{}')['@type'];
      if (types.includes(t)) el.remove();
    } catch (e) { /* leave anything unparsable alone */ }
  });
};

/**
 * Generate BreadcrumbList schema
 */
export const generateBreadcrumbs = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

/**
 * Inject BreadcrumbList JSON-LD into the page head.
 * Uses its OWN script id (breadcrumb-jsonld) so it coexists with the main
 * structured-data block (addStructuredData) instead of replacing it — pages
 * like Portfolio (ImageGallery), LocationPage (Service), and BlogPost (Article)
 * keep their schema AND get breadcrumbs. Multiple ld+json scripts are valid.
 */
export const addBreadcrumbSchema = (crumbs) => {
  if (typeof document === 'undefined' || !Array.isArray(crumbs) || crumbs.length === 0) return;
  const existing = document.getElementById('breadcrumb-jsonld');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'breadcrumb-jsonld';
  script.text = JSON.stringify(generateBreadcrumbs(crumbs));
  document.head.appendChild(script);
};

/**
 * Generate Service schema
 */
export const generateServiceSchema = (service) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.name,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Utah Realty Photos",
      "url": "https://www.utahrealtyphotos.com"
    },
    "description": service.description,
    "areaServed": {
      "@type": "State",
      "name": "Utah"
    },
    "offers": service.price ? {
      "@type": "Offer",
      "price": service.price,
      "priceCurrency": "USD"
    } : undefined
  };
};

/**
 * Generate ImageObject schema for portfolio images
 */
export const generateImageSchema = (image) => {
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": image.url,
    "description": image.description,
    "name": image.title,
    "creator": {
      "@type": "Organization",
      "name": "Utah Realty Photos"
    }
  };
};

// Per-route titles, descriptions and canonicals live in seo/routeMeta.js
// (2026-10-17), shared with the server renderer. The old `pageMeta` table here
// had drifted (titles over 60 characters, a location canonical built from the
// city NAME) and is gone.

/**
 * Analytics event tracking
 */
export const trackEvent = (category, action, label) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label
    });
  }
};

/**
 * Track conversions
 */
export const trackConversion = (type, value) => {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'G-N2CYQER24W',
      value: value,
      currency: 'USD',
      transaction_id: Date.now().toString()
    });
  }
  
  // Track in your own system
  trackEvent('Conversion', type, value);
};

export default {
  updatePageMeta,
  addStructuredData,
  generateBreadcrumbs,
  generateServiceSchema,
  generateImageSchema,
  trackEvent,
  trackConversion
};
